import DateRange from '../../core/dateRange'
import {SimpleBlueButton} from '../../core/button'
import ChooseTime from '../monitoring/ChooseTime'
import './index.scss'
import {useState, useMemo, useCallback, useEffect} from 'react'
import {addHours, addDays, differenceInHours, differenceInDays} from 'date-fns'
import useInterval from 'use-interval'
import moment from 'moment'
import {setActiveTimeIndex, setRangeSelected} from '../../../stores/monitoring'
import {useDispatch, useSelector} from 'react-redux'
import {timesDataFactory} from '../monitoring/times'
import renderSkeletonLoaders from '../../../utils/loader'
import {
    combine,
    DateFormats,
    formatDate,
    TZ,
    updateSeconds
} from '../../../utils/date'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../../utils/env'

const SELECTION = 'selection'
const HEARTBEAT_MONITORING = 'heartbeat_monitoring'
const RANGE = 'range'
const DAY = 'day'
const WEEK_TIME_INDEX = 5
const INITIAL_TIME_INDEX = 0
const ON_DEMAND = 'on-demand'
const HOUR = 'hour'
const FILTER = 'filter'
const MAX_ON_DEMAND_VALUE = 3
const MAX_HOUR_VALUE = 10
const DEFAULT_MS_VALUE = '00.000'

const calculateHeartbeatInterval = (hoursDifference, daysDifference, data) => {
    const currentInterval = data.find(item =>
        item.key.includes('h')
            ? item.value === hoursDifference
            : item.value === daysDifference * 24
    )
    return currentInterval?.interval || RANGE
}

const calculateDefaultInterval = (hoursDifference, daysDifference) => {
    if (hoursDifference <= MAX_ON_DEMAND_VALUE) return ON_DEMAND
    if (hoursDifference > MAX_ON_DEMAND_VALUE && daysDifference <= MAX_HOUR_VALUE) return HOUR
    return DAY
}

const DateInterval = ({
    callbackFn,
    resetFn,
    isAutoRefreshEnabled,
    setIsFiltering,
    showMs = false,
    type = null,
    isLoading = false
}) => {
    const dispatch = useDispatch()
    const text = useNamespaceTranslations('date-interval')
    const {rangeSelected, activeTimeIndex} = useSelector((state) => state.monitoring)
    const currentDate = new Date()

    const memoizedTimesData = useMemo(() =>
        timesDataFactory(type, text.base_times),
    [type, text.base_times]
    )

    const INITIAL_FILTERS = useMemo(() => {
        return {
            interval: memoizedTimesData?.[activeTimeIndex]?.interval,
            dates: [{startDate: currentDate, endDate: currentDate, key: SELECTION}],
            value: memoizedTimesData?.[activeTimeIndex]?.value,
            limit: memoizedTimesData?.[activeTimeIndex]?.limit || null
        }
    }, [memoizedTimesData, activeTimeIndex])

    const [filters, setFilters] = useState(INITIAL_FILTERS)
    const [startTime, setStartTime] = useState(currentDate)
    const [endTime, setEndTime] = useState(currentDate)
    const [startFloatSec, setStartFloatSec] = useState(DEFAULT_MS_VALUE)
    const [endFloatSec, setEndFloatSec] = useState(DEFAULT_MS_VALUE)

    const getStartDate = useCallback((interval, value) => {
        if (type === HEARTBEAT_MONITORING) {
            return filters.dates[0].startDate
        }
        return interval === DAY
            ? addDays(currentDate, -value)
            : addHours(currentDate, -value)
    },
    [filters.dates, type, currentDate]
    )

    const combineDateTime = useCallback((date, time, floatSec) => {
        const combined = combine(date, time)

        if (showMs && floatSec !== DEFAULT_MS_VALUE) {
            return updateSeconds(floatSec, combined, true)
        }

        return combined
    }, [showMs])

    const handleChangeFilters = useCallback((filterData) => {
        if (!filterData?.interval) {
            setFilters((prev) => ({
                ...prev,
                dates: filterData,
                actionType: SELECTION
            }))
            return
        }

        dispatch(setRangeSelected(false))

        const selectedTime = memoizedTimesData.find((time) => time.key === filterData.interval) || {}
        const newFilters = {
            interval: selectedTime.interval,
            dates: INITIAL_FILTERS.dates,
            value: selectedTime.value,
            limit: selectedTime.limit,
            actionType: FILTER
        }

        setFilters(newFilters)

        const startDate = getStartDate(newFilters.interval, newFilters.value)

        callbackFn({
            startDate: formatDate(startDate, DateFormats.ISO_DATETIME, TZ.UTC),
            endDate: formatDate(currentDate, DateFormats.ISO_DATETIME, TZ.UTC),
            interval: newFilters.interval,
            showLoader: true,
            limit: selectedTime.limit
        })
    }, [
        dispatch,
        memoizedTimesData,
        callbackFn,
        getStartDate,
        INITIAL_FILTERS
    ])

    const applyFilters = useCallback(() => {
        dispatch(setRangeSelected(true))
        dispatch(setActiveTimeIndex(
            type === HEARTBEAT_MONITORING ? WEEK_TIME_INDEX : INITIAL_TIME_INDEX
        ))

        const {startDate, endDate} = filters.dates[0]

        const modifiedStartDate = combineDateTime(startDate, startTime, startFloatSec)
        const modifiedEndDate = combineDateTime(endDate, endTime, endFloatSec)

        const startDateObj = new Date(modifiedStartDate)
        const endDateObj = new Date(modifiedEndDate)

        const hoursDifference = differenceInHours(endDateObj, startDateObj)
        const daysDifference = differenceInDays(endDateObj, startDateObj)

        callbackFn({
            startDate: modifiedStartDate,
            endDate: modifiedEndDate,
            interval: type === HEARTBEAT_MONITORING
                ? calculateHeartbeatInterval(hoursDifference, daysDifference, memoizedTimesData)
                : calculateDefaultInterval(hoursDifference, daysDifference),
            showLoader: true
        })
    }, [
        dispatch,
        filters.dates,
        type,
        combineDateTime,
        startTime,
        endTime,
        startFloatSec,
        endFloatSec,
        callbackFn,
        memoizedTimesData
    ])

    const resetFilters = useCallback(() => {
        dispatch(setRangeSelected(false))
        setStartTime(currentDate)
        setEndTime(currentDate)
        setStartFloatSec(DEFAULT_MS_VALUE)
        setEndFloatSec(DEFAULT_MS_VALUE)
        setFilters(INITIAL_FILTERS)
        resetFn()
    }, [dispatch, resetFn, INITIAL_FILTERS])

    const runAutoRefresh = useCallback(() => {
        const {interval, value, limit} = filters
        const startDate = getStartDate(interval, value)

        callbackFn({
            startDate: formatDate(startDate, DateFormats.ISO_DATETIME, TZ.UTC),
            endDate: formatDate(currentDate, DateFormats.ISO_DATETIME, TZ.UTC),
            interval,
            showLoader: false,
            limit
        })
    }, [filters, getStartDate, callbackFn, currentDate])

    useInterval(
        runAutoRefresh,
        isAutoRefreshEnabled && !rangeSelected
            ? envConfig.serverMonitoringliveUpdateTime
            : null
    )

    useEffect(() => {
        dispatch(setActiveTimeIndex(
            type === HEARTBEAT_MONITORING ? WEEK_TIME_INDEX : INITIAL_TIME_INDEX
        ))
    }, [dispatch, type])

    const startTimeMoment = useMemo(() => moment(startTime), [startTime])
    const endTimeMoment = useMemo(() => moment(endTime), [endTime])

    const handleStartTimeChange = useCallback((momentObj) => {
        setStartTime(momentObj.toDate())
    }, [])

    const handleEndTimeChange = useCallback((momentObj) => {
        setEndTime(momentObj.toDate())
    }, [])

    return (
        <>
            {isLoading ? (
                renderSkeletonLoaders(1)
            ) : (
                <ChooseTime
                    setIsFiltering={setIsFiltering}
                    filters={memoizedTimesData}
                    onChange={handleChangeFilters}
                />
            )}

            {isLoading ? (
                renderSkeletonLoaders(1, 'input-skeleton-sm')
            ) : (
                <DateRange
                    placeholder={text.date_range}
                    state={filters.dates}
                    onChange={handleChangeFilters}
                    hasTimePicker
                    showMs={showMs}
                    startFloatSec={startFloatSec}
                    endFloatSec={endFloatSec}
                    setStartFloatSec={setStartFloatSec}
                    setEndFloatSec={setEndFloatSec}
                    onChangeStartTime={handleStartTimeChange}
                    onChangeEndTime={handleEndTimeChange}
                    startTime={startTimeMoment}
                    endTime={endTimeMoment}
                >
                    <div>
                        <SimpleBlueButton outline onClick={resetFilters}>
                            {text.reset}
                        </SimpleBlueButton>
                        <SimpleBlueButton onClick={applyFilters}>
                            {text.apply}
                        </SimpleBlueButton>
                    </div>
                </DateRange>
            )}
        </>
    )
}

export default DateInterval
